import { ITenantApiKey } from '@pepperhq/types-sdk';
import React from 'react';
import {
    Alert,
    AlertTitle,
    alpha,
    Box,
    Button,
    Divider,
    IconButton,
    Paper,
    styled,
    Typography
} from '@mui/material';
import { MuiGrid } from 'lib/MuiGrid/MuiGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useGridSingleSelection } from 'lib/MuiGrid/useGridSingleSelection';
import { Row } from 'ui/Flex';
import { ActionDialog } from 'ui/dialogs/ActionDialog';
import { IApiKeyDetails } from './useApiKeys';
import { StatusCell } from 'ui/table/StatusCell';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';

type ApiKeyRow = ITenantApiKey<string> & { id: string };

const renderStatusCell = (params: GridRenderCellParams) => (
    <StatusCell
        value={params.value ? 'true' : 'false'}
        labelMap={{ true: 'Disabled', false: 'Active' }}
        colorMap={{ true: 'red', false: 'green' }}
        defaultColor="grey"
    />
);

interface ApiKeysPageProps {
    apiKeyDetails: IApiKeyDetails;
    onToggleAddOpen: () => void;
    onToggleApiKey: (applicationId: string, isDisabled: boolean) => void;
    onDeleteApiKey: (applicationId: string) => Promise<boolean>;
}

const StyledAlert = styled(Alert)(({ theme }) => ({
    marginTop: theme.spacing(2),
    borderColor: 'divider',
    backgroundColor: alpha(theme.palette.purple.tertiary, 0.5),
    color: theme.palette.text.secondary,
    '& .MuiAlert-message': { flex: 1, padding: theme.spacing(0.75, 0, 0, 0) },
    '& .MuiAlert-icon': { color: theme.palette.purple.core }
}));

export const ApiKeysPage: React.FC<ApiKeysPageProps> = ({
    apiKeyDetails,
    onToggleAddOpen,
    onToggleApiKey,
    onDeleteApiKey
}) => {
    const [showFullAlert, setShowFullAlert] = React.useState(true);
    const handleAlertToggle = React.useCallback(() => setShowFullAlert(value => !value), []);
    const rows = React.useMemo<ApiKeyRow[]>(
        () => (apiKeyDetails.keys ?? []).map(k => ({ ...k, id: k.applicationId })),
        [apiKeyDetails.keys]
    );

    const [selectedKey, rowSelectionModel, handleSelectRow] = useGridSingleSelection(rows);

    const actionsDisabled = !!apiKeyDetails.loadingKeyId || apiKeyDetails.listLoading;

    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

    const handleDisable = React.useCallback(() => {
        if (selectedKey) {
            onToggleApiKey(selectedKey.applicationId, true);
        }
    }, [selectedKey, onToggleApiKey]);

    const handleEnable = React.useCallback(() => {
        if (selectedKey) {
            onToggleApiKey(selectedKey.applicationId, false);
        }
    }, [selectedKey, onToggleApiKey]);

    const handleDeleteClick = React.useCallback(() => setDeleteDialogOpen(true), []);

    const handleDeleteClose = React.useCallback(() => setDeleteDialogOpen(false), []);

    const handleDeleteConfirm = React.useCallback(async () => {
        if (selectedKey) {
            const success = await onDeleteApiKey(selectedKey.applicationId);
            if (success) {
                setDeleteDialogOpen(false);
            }
        }
    }, [selectedKey, onDeleteApiKey]);

    const columns = React.useMemo<GridColDef[]>(
        () => [
            { field: 'description', headerName: 'Description', flex: 1 },
            { field: 'sessionId', headerName: 'Auth Token', flex: 2 },
            { field: 'applicationId', headerName: 'Application ID', flex: 1 },
            { field: 'isDisabled', headerName: 'Status', width: 110, renderCell: renderStatusCell }
        ],
        []
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            <StyledAlert
                severity="info"
                action={
                    <IconButton
                        color="inherit"
                        onClick={handleAlertToggle}
                        sx={{
                            transform: showFullAlert ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease-in-out'
                        }}
                    >
                        <KeyboardArrowDown />
                    </IconButton>
                }
            >
                <AlertTitle>
                    <Typography variant="subtitle1" fontWeight="bold">
                        API Key Usage
                    </Typography>
                </AlertTitle>
                {showFullAlert && (
                    <React.Fragment>
                        <Typography variant="body2" gutterBottom>
                            Each third-party integration should be issued its own API key and Application ID.
                            This lets you revoke access for a single integration without affecting others.
                        </Typography>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                            Third parties should include these headers in every API request to Pepper:
                        </Typography>
                        <Box
                            component="pre"
                            sx={{
                                width: '100%',
                                mt: 1,
                                mb: 1,
                                px: 2,
                                py: 1.5,
                                bgcolor: 'purple.tertiary',
                                color: 'text.primary',
                                borderRadius: 1,
                                fontSize: '0.8rem',
                                fontFamily: 'monospace',
                                whiteSpace: 'pre'
                            }}
                        >
                            {`Authorization: Token <auth-token>\nx-application-id: <application-id>`}
                        </Box>
                    </React.Fragment>
                )}
            </StyledAlert>
            <Box py={2}>
                <Row flex={1} align="space-between" valign="flex-end">
                    <Box />
                    <Row gutter>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={onToggleAddOpen}
                            disabled={actionsDisabled}
                        >
                            Create
                        </Button>
                        <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            onClick={handleEnable}
                            disabled={actionsDisabled || !selectedKey || !selectedKey.isDisabled}
                        >
                            Enable
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleDisable}
                            disabled={actionsDisabled || !selectedKey || selectedKey.isDisabled}
                        >
                            Disable
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleDeleteClick}
                            disabled={actionsDisabled || !selectedKey}
                        >
                            Delete
                        </Button>
                    </Row>
                </Row>
            </Box>
            <Paper elevation={0} sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
                <MuiGrid
                    columns={columns}
                    rows={rows}
                    loading={apiKeyDetails.listLoading}
                    disableMultipleRowSelection
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={handleSelectRow}
                    disableRowSelectionOnClick={false}
                />
            </Paper>
            <ActionDialog
                open={deleteDialogOpen}
                onClose={handleDeleteClose}
                onConfirm={handleDeleteConfirm}
                loading={!!apiKeyDetails.loadingKeyId}
                title={`Delete "${selectedKey?.description}"?`}
                content="This API key will be permanently deleted and any integrations using it will stop working."
            />
        </Box>
    );
};
